// Admin import-monitoring dashboard.
//
// Renders the latest run of each import pipeline as a read-only ops surface, built from the
// design-system components and following the AuditLog screen's conventions. Drill into a run to
// see its source file and its full error log (cursor-paged "Load more").
//
// The server stays authoritative: `session.role` gates the display only, and the endpoints
// re-check the role. Read-only (re-triggering imports is a platform-ops action). The server
// decides the scope from the signed-in role: a SystemAdministrator sees every tenant's runs, so
// for that role only the table gains a Tenant column to tell two tenants' runs of one feed
// apart; other admins see their own tenant's runs, as before.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppShell,
  Banner,
  Button,
  Card,
  StatusBadge,
  Table,
  type StatusTone,
  type TableColumn,
} from '../../design-system';
import { isAdminRole } from '../../auth/rbac';
import {
  getImportRun,
  listImportRuns,
  type ImportErrorCursor,
  type ImportRun,
  type ImportRunDetail,
  type ImportRunError,
} from '../../api/import-monitoring.client';
import styles from './ImportMonitoring.module.css';

// The drill-in leaves the error-log page size to the server default (bounded
// server-side). DISPLAY-ONLY hot-run tinting reads the LIVE failure-rate
// threshold off each run (`run.failureRateThreshold`) — the same configured AC3
// boundary the server alarm fires on — so the dashboard never diverges from the
// alarm when ops retunes IMPORT_FAILURE_RATE_THRESHOLD (Story 14.4 code review).

export interface ImportMonitoringProps {
  /** Non-authoritative session values; `role` drives the client-side display gate. */
  session?: { role?: string | null };
}

export function ImportMonitoring({ session }: ImportMonitoringProps) {
  const canView = isAdminRole(session?.role);
  // Only a SystemAdministrator's read spans tenants; for every other admin the tenant is their own.
  const seesEveryTenant = session?.role === 'SystemAdministrator';

  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const page = await listImportRuns();
      if (!mountedRef.current) return;
      setRuns(page);
    } catch {
      if (!mountedRef.current) return;
      setLoadError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    void loadRuns();
  }, [canView, loadRuns]);

  const tenantColumn: TableColumn<ImportRun>[] = seesEveryTenant
    ? [{ key: 'tenant', header: 'Tenant', render: (r) => r.tenantId }]
    : [];
  const columns: TableColumn<ImportRun>[] = [
    ...tenantColumn,
    { key: 'pipeline', header: 'Pipeline', render: (r) => pipelineLabel(r) },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge tone={statusTone(r)}>{statusLabel(r)}</StatusBadge>,
    },
    { key: 'startedAt', header: 'Started', render: (r) => r.startedAt ?? '—' },
    { key: 'completedAt', header: 'Completed', render: (r) => r.completedAt ?? '—' },
    { key: 'processed', header: 'Processed', render: (r) => String(r.processedRowCount) },
    {
      key: 'failed',
      header: 'Failed',
      render: (r) => (
        <span className={isHotRate(r) ? styles.hot : undefined}>
          {`${r.failedRowCount} (${formatRate(r.failureRate)})`}
        </span>
      ),
    },
    { key: 'errors', header: 'Errors', render: (r) => String(r.errorCount) },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <Button variant="secondary" onClick={() => setSelectedRunId(r.id)}>
          View errors
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <AppShell title="Import monitoring" subtitle="Read-only import pipeline status.">
        <Banner type="error" title="Access restricted">
          You don't have permission to view import monitoring.
        </Banner>
      </AppShell>
    );
  }

  if (selectedRunId !== null) {
    return (
      <RunDetail runId={selectedRunId} onBack={() => setSelectedRunId(null)} role={session?.role} />
    );
  }

  const problemRuns = runs.filter(isProblemRun);

  return (
    <AppShell
      title="Import monitoring"
      subtitle={
        seesEveryTenant
          ? 'The most recent run of each import pipeline, for every tenant.'
          : 'The most recent run of each import pipeline.'
      }
    >
      {loadError && runs.length === 0 ? (
        <Banner type="error" title="Couldn't load import monitoring">
          Please reload the page and try again.
        </Banner>
      ) : (
        <>
          {problemRuns.length > 0 ? (
            <Banner type="warning" title="Some pipelines need attention">
              {`${problemRuns.length} pipeline${problemRuns.length === 1 ? '' : 's'} recently failed, were rejected, or exceeded the failure-rate threshold. Open a run to see its error log.`}
            </Banner>
          ) : null}
          <Card
            title="Pipelines"
            subtitle="Newest run per pipeline. Open a run to drill into its error log."
          >
            {loading ? (
              <p role="status" className={styles.loading}>
                Loading import monitoring…
              </p>
            ) : (
              <Table
                caption="Import pipeline runs"
                hideCaption={false}
                columns={columns}
                rows={runs}
                rowKey={(r) => r.id}
                emptyMessage="No import runs yet."
              />
            )}
          </Card>
        </>
      )}
    </AppShell>
  );
}

/** The run drill-in: source file + the full error log (cursor "Load more"). */
function RunDetail({
  runId,
  onBack,
  role,
}: {
  runId: string;
  onBack: () => void;
  role?: string | null;
}) {
  const [detail, setDetail] = useState<ImportRunDetail | null>(null);
  const [errors, setErrors] = useState<ImportRunError[]>([]);
  const [nextCursor, setNextCursor] = useState<ImportErrorCursor | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Guard against a fast double-click firing two concurrent getImportRun calls
  // before the disabled state re-renders (Story 14.4 review P7). Protects both
  // the initial load and "Load more".
  const inFlightRef = useRef(false);

  const load = useCallback(
    async (cursor: ImportErrorCursor | null) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const fresh = cursor === null;
      if (fresh) {
        setLoading(true);
        setLoadError(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const page = await getImportRun(runId, cursor);
        if (!mountedRef.current) return;
        setDetail(page);
        setErrors((prev) => (fresh ? page.errors.entries : [...prev, ...page.errors.entries]));
        setNextCursor(page.errors.nextCursor);
        setLoadError(false);
      } catch {
        if (!mountedRef.current) return;
        setLoadError(true);
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [runId],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const loadMore = useCallback(() => {
    if (nextCursor === null) return;
    void load(nextCursor);
  }, [nextCursor, load]);

  const columns: TableColumn<ImportRunError>[] = [
    { key: 'createdAt', header: 'Recorded at', render: (e) => e.createdAt },
    { key: 'errorType', header: 'Type', render: (e) => e.errorType },
    {
      key: 'severity',
      header: 'Severity',
      render: (e) => (
        <StatusBadge tone={e.severity === 'ERROR' ? 'error' : 'warning'}>{e.severity}</StatusBadge>
      ),
    },
    {
      key: 'rowNumber',
      header: 'Row',
      render: (e) => (e.rowNumber === null ? '—' : String(e.rowNumber)),
    },
    { key: 'fieldName', header: 'Field', render: (e) => e.fieldName ?? '—' },
    { key: 'detail', header: 'Detail', render: (e) => e.detail ?? '—' },
  ];

  const run = detail?.run;

  return (
    <AppShell
      title="Import run"
      subtitle={run ? `${pipelineLabel(run)} — ${run.jobStatus}` : 'Import run detail'}
    >
      <div className={styles.backRow}>
        <Button variant="secondary" onClick={onBack}>
          ← Back to pipelines
        </Button>
      </div>

      {!isAdminRole(role) ? (
        <Banner type="error" title="Access restricted">
          You don't have permission to view import monitoring.
        </Banner>
      ) : loadError && detail === null ? (
        <Banner type="error" title="Couldn't load the import run">
          Please go back and try again.
        </Banner>
      ) : loading ? (
        <p role="status" className={styles.loading}>
          Loading import run…
        </p>
      ) : run && detail ? (
        <>
          {loadError ? (
            <Banner type="warning" title="Couldn't load more">
              The next page of errors couldn't be loaded. Please try again.
            </Banner>
          ) : null}
          <Card title="Run summary">
            <dl className={styles.summary}>
              <Summary label="Pipeline" value={pipelineLabel(run)} />
              <Summary label="Status" value={run.jobStatus} />
              <Summary label="Validation" value={run.validationStatus} />
              <Summary label="Started" value={run.startedAt ?? '—'} />
              <Summary label="Completed" value={run.completedAt ?? '—'} />
              <Summary label="Processed rows" value={String(run.processedRowCount)} />
              <Summary label="Applied rows" value={String(run.appliedRowCount)} />
              <Summary
                label="Failed rows"
                value={`${run.failedRowCount} (${formatRate(run.failureRate)})`}
              />
              <Summary label="Source file" value={detail.file.s3Key} />
            </dl>
          </Card>
          <Card title="Error log" subtitle="Every recorded error/warning for this run.">
            <Table
              caption="Import run errors"
              hideCaption={false}
              columns={columns}
              rows={errors}
              rowKey={(e) => e.id}
              emptyMessage="No errors recorded for this run."
            />
            {nextCursor !== null ? (
              <div className={styles.pagination}>
                <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            ) : null}
          </Card>
        </>
      ) : null}
    </AppShell>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.summaryRow}>
      <dt className={styles.summaryKey}>{label}</dt>
      <dd className={styles.summaryValue}>{value}</dd>
    </div>
  );
}

/** "CATALOGUE · Tenant-wide" or "AVAILABILITY · depot <id>". */
function pipelineLabel(run: ImportRun): string {
  return `${run.feedType} · ${run.depotId === null ? 'Tenant-wide' : `depot ${run.depotId}`}`;
}

/** The status as shown: a HELD run is a programme-detail file waiting for a confirmed schedule. */
function statusLabel(run: ImportRun): string {
  return run.jobStatus === 'HELD' ? 'HELD (awaiting a confirmed schedule)' : run.jobStatus;
}

/** Map a run's status (and failure rate) to a StatusBadge tone. */
function statusTone(run: ImportRun): StatusTone {
  if (run.jobStatus === 'REJECTED' || run.jobStatus === 'FAILED') return 'error';
  if (run.jobStatus === 'COMPLETED') return isHotRate(run) ? 'warning' : 'success';
  return 'neutral'; // PROCESSING, or HELD: waiting is not a problem
}

function isHotRate(run: ImportRun): boolean {
  // Strict `>` mirrors the server's exceedsFailureThreshold (AC3): a run AT the
  // threshold is not hot. Uses the run's live threshold, not a hardcoded mirror.
  return run.failureRate > run.failureRateThreshold;
}

/** A run worth flagging in the top banner: rejected, failed, or hot failure rate. */
function isProblemRun(run: ImportRun): boolean {
  return run.jobStatus === 'REJECTED' || run.jobStatus === 'FAILED' || isHotRate(run);
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}
